import { useSpaceIfAny } from './org'

/**
 * The civil date at a zone, as `YYYY-MM-DD`.
 *
 * A date is a fact about a place. `toISOString()` names the UTC day, which is
 * already tomorrow for a New York shul from about 8pm — so a screen that opens
 * "today" that way opens the wrong day every evening.
 *
 * Assembled from parts rather than formatted to a locale that happens to spell
 * dates this way: the shape is the contract here, since these strings are
 * compared against the server's own `YYYY-MM-DD`.
 */
export function todayIn(timeZoneId: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timeZoneId,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const at = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  return `${at('year')}-${at('month')}-${at('day')}`
}

/** The zone a browser is in, which is the reader's rather than the shul's. */
function readerZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * Today at the shul — the one a screen names, or else the one being worked in.
 *
 * The shul's zone, not the reader's: a gabbai on a trip is still publishing
 * that shul's luach, and the day a sheet is about is the day it is there.
 *
 * A screen that knows its shul passes the zone, which is the only answer a
 * public page has: a visitor is working in no space at all. Where neither is
 * known yet — the org still loading — this answers with the reader's own zone,
 * the closest thing available and right for everybody who is not traveling.
 */
export function useToday(timeZoneId?: string): string {
  const space = useSpaceIfAny()
  const active = space?.kind === 'org' ? space.spaceId : null
  const zone =
    timeZoneId ?? space?.orgs.find((o) => o.spaceId === active)?.timeZoneId
  return todayIn(zone ?? readerZone())
}

/**
 * A civil date as somebody reads it back, with no zone to shift it.
 *
 * `YYYY-MM-DD` carries no time, so handing it to `new Date()` and formatting in
 * the reader's zone moves it a day for everybody west of UTC. The parts are
 * read out and re-formatted as UTC, which is the only zone in which a bare
 * civil date means the day it says.
 */
export function readCivilDate(
  iso: string,
  month: 'short' | 'long' = 'short',
): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month,
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * A civil date as a sentence names it — `Wednesday, September 23`, or the
 * weekday alone. English, because the sentences it is set into are; read as
 * UTC for the reason `readCivilDate` is.
 */
export function civilDay(iso: string, form: 'weekday' | 'long'): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    ...(form === 'long' ? { month: 'long', day: 'numeric' } : {}),
  })
}

/**
 * A civil date's weekday as a luach writes it — `יום ו׳`, and Shabbos by its
 * name, since no luach calls it the seventh day.
 */
const WEEKDAY_HE = [
  'יום א׳',
  'יום ב׳',
  'יום ג׳',
  'יום ד׳',
  'יום ה׳',
  'יום ו׳',
  'שבת',
] as const

export function civilWeekdayHe(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return iso
  return WEEKDAY_HE[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]
}
